import React from 'react';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from './LanguageContext';

const AboutSection = () => {
  const { language, t, getLocalizedPath } = useLanguage();

  const benefits = language === 'da' ? [
    'Nøje udvalgte hoteller',
    'Reel erfaring',
    'Testet i praksis',
    'Tryghed fra start til slut'
  ] : [
    'Carefully selected hotels',
    'Real experience',
    'Tested in practice',
    'Peace of mind from start to finish'
  ];

  return (
    <section className="py-24 bg-stone-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative lg:pr-12"
          >
            <div className="relative z-10 rounded-[3rem] overflow-hidden shadow-2xl border-8 border-white max-w-sm mx-auto lg:mx-0">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/alora-rejser-om-os.png" 
                srcSet="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/alora-rejser-om-os.png 400w, https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/alora-rejser-om-os.png 800w"
                sizes="(max-width: 768px) 384px, 400px"
                alt="Alora Rejser" 
                className="w-full aspect-[4/5] object-cover object-top hover:scale-105 transition-transform duration-700"
              />
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-8 right-4 bg-[#99A18C] text-white p-8 rounded-3xl shadow-2xl z-20 hidden md:block max-w-[240px]">
              <p className="text-2xl font-serif italic mb-2">
                {language === 'da' ? '10+ år' : '10+ years'}
              </p>
              <p className="text-sm text-emerald-50/90 leading-relaxed font-medium">
                {language === 'da' ? 'Rejseerfaring med fokus på tilgængelighed' : 'Travel experience with focus on accessibility'}
              </p>
            </div>
            {/* Background decorative element */}
            <div className="absolute -top-12 -left-12 w-64 h-64 bg-[#99A18C]/10 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#99A18C] font-bold uppercase tracking-widest text-xs mb-4 block">
              {language === 'da' ? 'Hjertet bag bureauet' : 'The Heart Behind the Agency'}
            </span>
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-slate-800 mb-8 leading-tight italic">
              {language === 'da' ? 'Født ud af et ønske om bedre handicaprejser' : 'Born Out of a Desire for Better Accessible Travel'}
            </h2>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              {language === 'da' ? (
                "Alora Rejser blev skabt med et ønske om at gøre handicaprejser nemmere og mere overskuelige for kørestolsbrugere og gangbesværede – på lige fod med alle andre."
              ) : (
                "Alora Rejser was created with a desire to make accessible travel easier and more manageable for wheelchair users and people with reduced mobility – on equal terms with everyone else."
              )}
            </p>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              {language === 'da' ? (
                "I over 10 år har jeg rejst sammen med min mand, som er fast kørestolsbruger. Det har givet mig en særlig forståelse for de udfordringer, der kan opstå, når man skal finde handicapvenlige rejser, der fungerer i praksis – ikke kun på papiret."
              ) : (
                "For over 10 years, I have traveled with my husband, who is a full-time wheelchair user. This has given me a special understanding of the challenges that can arise when finding wheelchair friendly holidays that work in practice – not just on paper."
              )}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-[#99A18C]/10 rounded-full flex items-center justify-center">
                    <CheckCircle2 size={14} className="text-[#99A18C]" />
                  </div>
                  <span className="text-slate-700 font-medium">{benefit}</span>
                </div>
              ))}
            </div>

            <Link 
              to={getLocalizedPath('about', language)} 
              className="inline-flex items-center gap-3 bg-[#99A18C] text-white px-8 py-4 rounded-full font-bold hover:bg-[#8a917e] transition-all shadow-lg hover:shadow-[#99A18C]/20 group"
            >
              {language === 'da' ? 'Læs hele historien' : 'Read the Full Story'}
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
